import React, { ChangeEvent, FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ControllerRenderProps,
  FieldError,
  UseFormMethods,
} from 'react-hook-form';

import { EmailModuleFormValues, FIELDS } from './form';
import {
  formatOtherRecipients,
  MAX_OTHER_RECIPIENTS,
  otherRecipientsToInputValue,
  parseOtherRecipients,
} from './otherRecipients';
import {
  StyledErrorMessage,
  StyledLabel,
  StyledWrapper,
} from 'components/Inputs/styled';
import { InputWrapper, StyledInput } from 'components/Inputs/TextInput/styled';

interface OtherRecipientsFieldProps
  extends Pick<
    UseFormMethods<EmailModuleFormValues>,
    'setError' | 'clearErrors' | 'trigger'
  > {
  field: ControllerRenderProps<EmailModuleFormValues>;
  error?: FieldError;
}

const OtherRecipientsField: FC<OtherRecipientsFieldProps> = ({
  field,
  error,
  setError,
  clearErrors,
  trigger,
}) => {
  const { t } = useTranslation();
  const value = otherRecipientsToInputValue(field.value);
  const addresses = parseOtherRecipients(field.value);
  const count = addresses.length;
  const maxErrorMessage = t('email.otherRecipientsMax', {
    max: MAX_OTHER_RECIPIENTS,
  });

  useEffect(() => {
    if (count <= MAX_OTHER_RECIPIENTS && error?.message === maxErrorMessage) {
      clearErrors(FIELDS.OTHER_RECIPIENTS);
    }
  }, [count, error?.message, clearErrors, maxErrorMessage]);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const nextValue = e.target.value;
      const parsed = parseOtherRecipients(nextValue);

      if (parsed.length > MAX_OTHER_RECIPIENTS) {
        setError(FIELDS.OTHER_RECIPIENTS, {
          type: 'max',
          message: maxErrorMessage,
        });
        field.onChange(
          formatOtherRecipients(parsed.slice(0, MAX_OTHER_RECIPIENTS)),
        );
        return;
      }

      if (error?.type === 'max') {
        clearErrors(FIELDS.OTHER_RECIPIENTS);
      }

      field.onChange(nextValue);
    },
    [field, setError, clearErrors, error?.type, maxErrorMessage],
  );

  const handleBlur = useCallback(() => {
    field.onBlur();
    trigger(FIELDS.OTHER_RECIPIENTS);
  }, [field, trigger]);

  return (
    <StyledWrapper>
      <StyledLabel htmlFor={field.name} error={error}>
        {t('email.otherRecipients')}
      </StyledLabel>
      <InputWrapper>
        <StyledInput
          width="100%"
          id={field.name}
          name={field.name}
          type="text"
          error={error}
          ref={field.ref}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      </InputWrapper>
      {error && <StyledErrorMessage>{error.message}</StyledErrorMessage>}
    </StyledWrapper>
  );
};

export default OtherRecipientsField;
